import { UPDATE_ROUTE, RESET_ROUTES } from 'src/components/Router/reducer';
import { EDIT } from 'src/consts';
import type { Action, Mode } from 'src/types';

import { createResumeRoutes } from './routes';
import type { CreateResumeRoute } from './types';

export enum CreateResumeActions {
  UPDATE_ACTIVE_ROUTE = 'UPDATE_ACTIVE_ROUTE',
  SET_ROUTE_AS_TOUCHED = 'SET_ROUTE_AS_TOUCHED',
  SET_ALL_ROUTES_AS_TOUCHED = 'SET_ALL_ROUTES_AS_TOUCHED',
  UPDATE_CREATE_STEP = 'UPDATE_CREATE_STEP',
  CREATE_RESUME_RENDERED = 'CREATE_RESUME_RENDERED',
  RETURN_TO_NON_PROFILE_PAGE = 'RETURN_TO_NON_PROFILE_PAGE',
}

export enum CreateStep {
  PROFILE_REVIEW = 'PROFILE_REVIEW',
  RESUME_LIMIT_REACHED = 'RESUME_LIMIT_REACHED',
  RESUME_CREATED = 'RESUME_CREATED',
}

export interface CreateResumeState {
  pages: Record<
    string,
    {
      touched: boolean;
    }
  >;
  inView?: CreateResumeRoute;
  createStep: CreateStep;
}

const initialState: CreateResumeState = {
  pages: {},
  createStep: CreateStep.PROFILE_REVIEW,
};

createResumeRoutes.forEach((route) => {
  initialState.pages[route] = {
    touched: false,
  };
});

export type CreateResumeAction =
  | (Action<typeof UPDATE_ROUTE> & {
      payload: {
        mode: Mode;
        to: CreateResumeRoute;
      };
    })
  | (Action<CreateResumeActions.UPDATE_ACTIVE_ROUTE> & {
      payload: CreateResumeRoute;
    })
  | (Action<CreateResumeActions.SET_ROUTE_AS_TOUCHED> & {
      payload: CreateResumeRoute;
    })
  | Action<CreateResumeActions.SET_ALL_ROUTES_AS_TOUCHED>
  | (Action<CreateResumeActions.UPDATE_CREATE_STEP> & {
      payload: { from: CreateStep; to: CreateStep };
    })
  | Action<typeof RESET_ROUTES>
  | Action<CreateResumeActions.CREATE_RESUME_RENDERED>
  | Action<CreateResumeActions.RETURN_TO_NON_PROFILE_PAGE>
  | Action<CreateResumeActions.RETURN_TO_NON_PROFILE_PAGE>;

export const createResumeReducer = (
  state = initialState,
  action: CreateResumeAction,
): CreateResumeState => {
  switch (action.type) {
    case UPDATE_ROUTE: {
      const { mode, to } = action.payload;

      if (createResumeRoutes.indexOf(to) !== -1 && mode === EDIT) {
        return {
          ...state,
          pages: {
            ...state.pages,
            [to]: {
              ...state.pages[to],
              touched: true,
            },
          },
        };
      }

      return {
        ...state,
      };
    }

    case CreateResumeActions.UPDATE_ACTIVE_ROUTE: {
      const to = action.payload;

      return {
        ...state,
        inView: to,
      };
    }

    case CreateResumeActions.SET_ROUTE_AS_TOUCHED: {
      const to = action.payload;

      return {
        ...state,
        pages: {
          ...state.pages,
          [to]: {
            ...state.pages[to],
            touched: true,
          },
        },
      };
    }

    case CreateResumeActions.SET_ALL_ROUTES_AS_TOUCHED: {
      const newPageState = { ...state.pages };

      Object.keys(newPageState).map((page) => {
        newPageState[page].touched = true;
      });

      return {
        ...state,
        pages: {
          ...newPageState,
        },
      };
    }

    case CreateResumeActions.UPDATE_CREATE_STEP: {
      const { to: createStep } = action.payload;

      return {
        ...state,
        createStep,
      };
    }

    case RESET_ROUTES: {
      return initialState;
    }

    default:
      return state;
  }
};
